import EditIcon from '@mui/icons-material/Edit';

import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useMemo } from 'react';

import {
	InventoryItemCondition,
	InventoryListingStatus,
	//InventoryItemMetric, TODO: tristen - uncomment after views are fixed
} from '@ekhodealer/ekho-common';
import {
	ActionMenu,
	ActionMenuItem,
	ActionMenuTrigger,
	Button,
	ContentWithTooltip,
	EmptySearchResults,
	Group,
	MoreButton,
	Pagination,
	SkeletonRect,
	Tag,
	Text,
} from '@ekhodealer/ekho-common/rev';
import { transformDisplayName } from '@ekhodealer/ekho-common/utils';

import type { TableColumn } from '@common/components/Table/Table';
import type { InventoryItemSearchRecord, ServicesProviderInventory } from '@ekhodealer/ekho-common';
import type { SortDirection } from '@ekhodealer/ekho-common/rev';

import { FloatingActionMenu } from '@common/components/FloatingActionMenu';
import { Bin24, Checkmark24 } from '@common/components/icons';
import { Table } from '@common/components/Table/Table';
import { useCurrentServicesProvider } from '@common/hooks/useCurrentServicesProvider';
import { PLACEHOLDER } from '@common/lib/strings';

import { useGetInventory } from '../api/useGetInventory';
import { usePrefetchUnpublishedInventory } from '../api/usePrefetchUnpublishedInventory';
import {
	currentInventoryListAtom,
	currentPageAtom,
	hasAppliedFiltersAtom,
	inventoryParamsAtom,
	InventoryTab,
	itemsPerPageAtom,
	resetAllFiltersAtom,
	sortConfigAtom,
	updateSearchValueAtom,
	updateSortAtom,
} from '../atoms/inventoryAtoms';
import {
	clearInventorySelectionAtom,
	currentPageItemsCountAtom,
	selectedInventoryCountAtom,
	selectedInventoryIdsAtom,
	updateSelectedInventoryAtom,
} from '../atoms/inventorySelectionAtoms';
import { useInventoryNavigation } from '../hooks/useInventoryNavigation';
import { useSelectedInventoryIds } from '../hooks/useSelectedInventoryIds';
import { InventoryTableColumnId } from '../lib/enums';
import {
	getFulfillmentOptionsLabel,
	getInventoryItemConditionLabel,
	getListingStatusTagProps,
	getVehicleMatchTagProps,
} from '../lib/utils';

import { ArchiveInventoryModal } from './bulkActionModals/ArchiveInventoryModal';
import { EnableOnlineSalesModal } from './bulkActionModals/EnableOnlineSalesModal';
import { PublishConfirmationModal } from './bulkActionModals/PublishConfirmationModal';
import { SyncConfirmationModal } from './bulkActionModals/SyncConfirmationModal';
import {
	useArchiveActions,
	useEnableOnlineSalesActions,
	usePublishActions,
	useSyncActions,
} from './actions';
import { PublishBlockers } from './PublishBlockers';

import './InventoryTable.scss';

// Vehicle info cell component
const VehicleInfoCell = ({ item }: { item: InventoryItemSearchRecord }) => {
	// Create display name from year, make, and model, then apply transform
	const displayName =
		item.modelYear && item.make && item.model
			? transformDisplayName(`${item.modelYear} ${item.make} ${item.model}`)
			: item.make && item.model
				? transformDisplayName(`${item.make} ${item.model}`)
				: item.model || item.make || '';

	return (
		<div>
			<Text size='bodySmall' weight='semibold'>
				{displayName}
			</Text>
			<Text size='footnote' weight='regular'>
				{item.vin}
			</Text>
		</div>
	);
};

// Actions menu cell component
const ActionMenuCell = ({
	item,
	onEdit,
}: {
	item: InventoryItemSearchRecord;
	onEdit: (item: InventoryItemSearchRecord) => void;
}) => {
	return (
		<ActionMenuTrigger>
			<MoreButton
				aria-label={`More actions for ${transformDisplayName(
					item.modelYear && item.make && item.model
						? `${item.modelYear} ${item.make} ${item.model}`
						: item.make && item.model
							? `${item.make} ${item.model}`
							: item.model || item.make || 'vehicle',
				)}`}
			/>
			<ActionMenu>
				<ActionMenuItem
					label='Edit'
					leadingAccessory={<EditIcon />}
					onAction={() => {
						onEdit(item);
					}}
				/>
			</ActionMenu>
		</ActionMenuTrigger>
	);
};

// Centralized cell rendering function that handles isPending states
const renderCell = (
	columnId: InventoryTableColumnId,
	item: InventoryItemSearchRecord,
	isLoading: boolean,
	options?: {
		onEdit?: (item: InventoryItemSearchRecord) => void;
		servicesProvider?: ServicesProviderInventory | null;
	},
): React.ReactNode => {
	if (isLoading) {
		return <SkeletonRect width={100} height={24} />;
	}

	switch (columnId) {
		case InventoryTableColumnId.VEHICLE:
			return <VehicleInfoCell item={item} />;
		case InventoryTableColumnId.STATUS: {
			const listingStatus = item.listing?.status || InventoryListingStatus.INCOMPLETE_DRAFT;
			const isBlocked =
				listingStatus === InventoryListingStatus.INCOMPLETE_DRAFT ||
				listingStatus === InventoryListingStatus.LOCKED;
			const statusProps = getListingStatusTagProps(
				item.listing?.status || InventoryListingStatus.INCOMPLETE_DRAFT,
			);
			const tag = (
				<Tag
					size='medium'
					accent={statusProps.accent}
					children={statusProps.children}
					indicator={statusProps.indicator}
					icon={statusProps.icon}
					iconSide={statusProps.iconSide}
				/>
			);

			return (
				<div style={{ minWidth: '140px' }}>
					{isBlocked ? (
						<div style={{ display: 'inline-block' }}>
							<ContentWithTooltip
								content={tag}
								tooltipContent={<PublishBlockers inventoryItemId={item.id} />}
								tooltipProps={{ placement: 'right' }}
							/>
						</div>
					) : (
						tag
					)}
				</div>
			);
		}
		case InventoryTableColumnId.ONLINE_SALES:
			return (
				<div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
					<span>{item.isMarkedForOnlineSale ? 'Enabled' : 'Lead gen'}</span>
					{item.isMarkedForOnlineSale && <Checkmark24 height={18} width={18} />}
				</div>
			);
		case InventoryTableColumnId.VEHICLE_MATCH: {
			const matchProps = getVehicleMatchTagProps(item.matching?.status);
			return <span>{matchProps.children}</span>;
		}
		// TODO: Rohan - uncomment after syncStatusResolvedAt is added to search record
		// case InventoryTableColumnId.LAST_SYNCED: {
		// 	return (
		// 		<div style={{ minWidth: '130px' }}>
		// 			{item.syncStatusResolvedAt
		// 				? moment(item.syncStatusResolvedAt).fromNow()
		// 				: PLACEHOLDER}
		// 		</div>
		// 	);
		// }
		case InventoryTableColumnId.STOCK_NUMBER: {
			return item.stockNumber || PLACEHOLDER;
		}
		case InventoryTableColumnId.VIN: {
			return item.vin || PLACEHOLDER;
		}
		case InventoryTableColumnId.MAKE:
			return item.make || PLACEHOLDER;
		case InventoryTableColumnId.CATEGORY:
			return item.vehicleCategory || PLACEHOLDER;
		case InventoryTableColumnId.YEAR:
			return item.modelYear || PLACEHOLDER;
		case InventoryTableColumnId.ODOMETER:
			return item.odometerReading
				? `${item.odometerReading.toLocaleString()} ${item.odometerUnit || ''}`.trim()
				: PLACEHOLDER;
		case InventoryTableColumnId.COLOR:
			return item.color || PLACEHOLDER;
		case InventoryTableColumnId.CONDITION:
			return getInventoryItemConditionLabel(item.newUsed || InventoryItemCondition.UNDEFINED);
		case InventoryTableColumnId.INVENTORY_AGE: {
			if (!item.dateAddedToInventory) {
				return <div style={{ minWidth: '140px' }}>{PLACEHOLDER}</div>;
			}
			const daysDiff = Math.floor(
				(new Date().getTime() - new Date(item.dateAddedToInventory).getTime()) /
					(1000 * 60 * 60 * 24),
			);
			return <div style={{ minWidth: '140px' }}>{`${daysDiff} days`}</div>;
		}
		case InventoryTableColumnId.FULFILLMENT_OPTIONS:
			return getFulfillmentOptionsLabel(item.fulfillmentOptions, options?.servicesProvider);
		case InventoryTableColumnId.MAP: {
			const mapValue = item.pricing?.map;
			return mapValue ? '✓' : PLACEHOLDER;
		}
		case InventoryTableColumnId.ADVERTISED: {
			const advertisedPrice = item.pricing?.advertisedPrice;
			return advertisedPrice ? `$${Number(advertisedPrice).toLocaleString()}` : PLACEHOLDER;
		}
		case InventoryTableColumnId.RETAIL: {
			const retailPrice = item.pricing?.retailPrice;
			return retailPrice ? `$${Number(retailPrice).toLocaleString()}` : PLACEHOLDER;
		}
		// TODO: TRISTEN - uncomment after views are fixed
		// case InventoryTableColumnId.VIEWS_24HR: {
		// 	const views = item.metrics?.[InventoryItemMetric.LAST_24_HOURS_VIEWS];
		// 	return views !== undefined ? views.toLocaleString() : PLACEHOLDER;
		// }
		case InventoryTableColumnId.ACTIONS:
			return <ActionMenuCell item={item} onEdit={options?.onEdit || (() => {})} />;
		default:
			return PLACEHOLDER;
	}
};

export const InventoryTable = () => {
	// Navigation hook for opening drawer
	const { openInventoryDetails } = useInventoryNavigation();

	// Table state atoms
	const params = useAtomValue(inventoryParamsAtom);
	const sortConfig = useAtomValue(sortConfigAtom);
	const updateSort = useSetAtom(updateSortAtom);
	const [currentPage, setCurrentPage] = useAtom(currentPageAtom);
	const [itemsPerPage, setItemsPerPage] = useAtom(itemsPerPageAtom);
	const resetAllFilters = useSetAtom(resetAllFiltersAtom);
	const updateSearchValue = useSetAtom(updateSearchValueAtom);
	const hasAppliedFilters = useAtomValue(hasAppliedFiltersAtom);

	// Selection atoms
	const selectedInventory = useAtomValue(selectedInventoryIdsAtom);
	const updateSelectedInventory = useSetAtom(updateSelectedInventoryAtom);
	const clearSelection = useSetAtom(clearInventorySelectionAtom);
	const setCurrentPageItemsCount = useSetAtom(currentPageItemsCountAtom);
	const selectedCount = useAtomValue(selectedInventoryCountAtom);
	const selectedIds = useSelectedInventoryIds();

	// Services provider for fulfillment options check
	const { data: servicesProvider } = useCurrentServicesProvider() as {
		data: ServicesProviderInventory | undefined;
	};

	// React query hook to get inventory data
	const { data: getInventoryResponse, isPending } = useGetInventory(params);

	// Extract items and count from the response
	const items = useMemo(() => getInventoryResponse?.data || [], [getInventoryResponse]);
	const queryCount = getInventoryResponse?.count || 0;

	// Prefetch non-published items for faster drawer loading
	usePrefetchUnpublishedInventory(items, !isPending);

	// Update current page items count atom when items change
	// This is used to calculate selected count when 'all' is selected
	const setCurrentInventoryList = useSetAtom(currentInventoryListAtom);
	useEffect(() => {
		setCurrentPageItemsCount(items.length);
		// Update current inventory list for navigation
		setCurrentInventoryList(items.map((item) => item.id));
	}, [items, setCurrentPageItemsCount, setCurrentInventoryList]);

	// Action hooks
	const {
		isPublishModalOpen,
		setIsPublishModalOpen,
		publishInitiatedCount,
		isPublishingSelected,
		handleOpenPublishModal,
		handlePublishConfirm,
		handlePublishCancel,
	} = usePublishActions({ selectedIds, selectedCount });
	const {
		isSyncModalOpen,
		setIsSyncModalOpen,
		syncInitiatedCount,
		isSyncing,
		handleOpenSyncModal,
		handleSyncConfirm,
		handleSyncCancel,
	} = useSyncActions({ selectedIds });
	const {
		isEnableOnlineSalesModalOpen,
		setIsEnableOnlineSalesModalOpen,
		enableInitiatedCount,
		isEnablingOnlineSales,
		handleOpenEnableOnlineSalesModal,
		handleEnableOnlineSalesConfirm,
		handleEnableOnlineSalesCancel,
	} = useEnableOnlineSalesActions({ selectedIds });
	const {
		isArchiveModalOpen,
		setIsArchiveModalOpen,
		isArchiving,
		handleOpenArchiveModal,
		handleArchiveConfirm,
		handleArchiveCancel,
	} = useArchiveActions({ selectedIds });

	// State update handlers
	const handleEditItem = (item: InventoryItemSearchRecord) => {
		openInventoryDetails(item.id, InventoryTab.VEHICLE_DETAILS);
	};
	const handleRowActivate = (item: InventoryItemSearchRecord) => {
		if (selectedCount === 0) {
			openInventoryDetails(item.id, InventoryTab.VEHICLE_DETAILS);
		}
	};
	const handleItemsPerPageChange = (newItemsPerPage: number) => {
		setItemsPerPage(newItemsPerPage);
		setCurrentPage(1); // Reset to first page when changing items per page
		clearSelection(); // Clear selection when changing items per page
	};
	const handleSort = (columnId: string, direction: SortDirection) => {
		updateSort({ columnId, direction });
	};
	const handlePageChange = (page: number) => {
		setCurrentPage(page);
		clearSelection(); // Clear selection when changing pages
	};

	const getSortDirection = (columnId: string): SortDirection => {
		const sort = sortConfig.find((s) => s.column === columnId);
		return sort?.direction || 'none';
	};

	const createSortableHeader =
		(title: string, tooltip?: string) =>
		(sortingProps?: {
			sortable?: boolean;
			sortDirection?: SortDirection;
			onSort?: (direction: SortDirection) => void;
		}) => {
			const { sortable, sortDirection, onSort } = sortingProps || {};

			const getNextSortDirection = (currentDirection: SortDirection): SortDirection => {
				switch (currentDirection) {
					case 'none':
						return 'ascending';
					case 'ascending':
						return 'descending';
					case 'descending':
						return 'none';
					default:
						return 'ascending';
				}
			};

			const headerContent = (
				<div className='table-header-title'>
					<span>{title}</span>
					{sortable && (
						<button
							onClick={() => onSort?.(getNextSortDirection(sortDirection || 'none'))}
							style={{
								background: 'none',
								border: 'none',
								cursor: 'pointer',
								fontSize: '12px',
								padding: '2px 4px',
							}}>
							{sortDirection === 'ascending'
								? '↑'
								: sortDirection === 'descending'
									? '↓'
									: '↕'}
						</button>
					)}
				</div>
			);

			if (tooltip) {
				return (
					<div className='inventory-table-header-with-tooltip'>
						<ContentWithTooltip
							content={headerContent}
							tooltipContent={tooltip}
							tooltipProps={{ placement: 'top' }}
						/>
					</div>
				);
			}

			return headerContent;
		};

	const columns: TableColumn<InventoryItemSearchRecord>[] = [
		{
			id: InventoryTableColumnId.VEHICLE,
			title: 'Vehicle Name',
			sortable: false,
			sortDirection: getSortDirection('make'),
			onSort: (direction) => handleSort('make', direction),
			renderCell: (item) => renderCell(InventoryTableColumnId.VEHICLE, item, isPending),
			renderHeader: createSortableHeader('Vehicle Name'),
			maxWidth: 300,
			minWidth: 200,
		},
		{
			id: InventoryTableColumnId.STATUS,
			title: 'Publish status',
			sortable: false,
			sortDirection: getSortDirection('listing.status'),
			onSort: (direction) => handleSort('listing.status', direction),
			renderCell: (item) => renderCell(InventoryTableColumnId.STATUS, item, isPending),
			renderHeader: createSortableHeader(
				'Publish status',
				'Shows whether this item is published, in draft, or blocked due to missing or syncing information.',
			),
			maxWidth: 120,
		},
		{
			id: InventoryTableColumnId.ONLINE_SALES,
			title: 'Online sales',
			sortable: false,
			sortDirection: getSortDirection('primarySource.system'),
			onSort: (direction) => handleSort('primarySource.system', direction),
			renderCell: (item) => renderCell(InventoryTableColumnId.ONLINE_SALES, item, isPending),
			renderHeader: createSortableHeader(
				'Online sales',
				'Indicates whether this item is shown as available for online sale.',
			),
			minWidth: 120,
		},
		// TODO: Rohan - uncomment after syncStatusResolvedAt is added to search record
		// {
		// 	id: InventoryTableColumnId.LAST_SYNCED,
		// 	title: 'Last synced',
		// 	sortable: false,
		// 	sortDirection: getSortDirection('resolvedAt'),
		// 	onSort: (direction) => handleSort('resolvedAt', direction),
		// 	renderCell: (item) => renderCell(InventoryTableColumnId.LAST_SYNCED, item, isPending),
		// 	renderHeader: createSortableHeader('Last sync'),
		// },
		{
			id: InventoryTableColumnId.INVENTORY_AGE,
			title: 'Inventory age',
			sortable: false,
			sortDirection: getSortDirection('dateAddedToInventory'),
			onSort: (direction) => handleSort('dateAddedToInventory', direction),
			renderCell: (item) => renderCell(InventoryTableColumnId.INVENTORY_AGE, item, isPending),
			renderHeader: createSortableHeader('Inventory age'),
		},
		{
			id: InventoryTableColumnId.ADVERTISED,
			title: 'Advertised',
			sortable: false,
			sortDirection: getSortDirection('pricing.advertisedPrice'),
			onSort: (direction) => handleSort('pricing.advertisedPrice', direction),
			renderCell: (item) => renderCell(InventoryTableColumnId.ADVERTISED, item, isPending),
			renderHeader: createSortableHeader('Advertised'),
		},
		{
			id: InventoryTableColumnId.VEHICLE_MATCH,
			title: 'Vehicle match',
			sortable: false,
			sortDirection: getSortDirection('matching.status'),
			onSort: (direction) => handleSort('matching.status', direction),
			renderCell: (item) => renderCell(InventoryTableColumnId.VEHICLE_MATCH, item, isPending),
			renderHeader: createSortableHeader(
				'Vehicle match',
				'Shows the progress of matching this item to a vehicle model.',
			),
			minWidth: 140,
		},
		// TODO: TRISTEN - uncomment after views are fixed
		// {
		// 	id: InventoryTableColumnId.VIEWS_24HR,
		// 	title: 'Views (24 Hr)',
		// 	sortable: false,
		// 	sortDirection: getSortDirection('metrics.LAST_24_HOURS_VIEWS'),
		// 	onSort: (direction) => handleSort('metrics.LAST_24_HOURS_VIEWS', direction),
		// 	renderCell: (item) => renderCell(InventoryTableColumnId.VIEWS_24HR, item, isPending),
		// 	renderHeader: createSortableHeader('Views (24h)', 'Shows the number of views in the last 24 hours. This value can take up to 12 hours to update.'),
		// },
		{
			id: InventoryTableColumnId.MAKE,
			title: 'Make',
			sortable: false,
			sortDirection: getSortDirection('make'),
			onSort: (direction) => handleSort('make', direction),
			renderCell: (item) => renderCell(InventoryTableColumnId.MAKE, item, isPending),
			renderHeader: createSortableHeader('Make'),
			minWidth: 100,
		},
		{
			id: InventoryTableColumnId.YEAR,
			title: 'Year',
			sortable: false,
			sortDirection: getSortDirection('modelYear'),
			onSort: (direction) => handleSort('modelYear', direction),
			renderCell: (item) => renderCell(InventoryTableColumnId.YEAR, item, isPending),
			renderHeader: createSortableHeader('Year'),
		},
		{
			id: InventoryTableColumnId.CONDITION,
			title: 'Condition',
			sortable: false,
			sortDirection: getSortDirection('newUsed'),
			onSort: (direction) => handleSort('newUsed', direction),
			renderCell: (item) => renderCell(InventoryTableColumnId.CONDITION, item, isPending),
			renderHeader: createSortableHeader('Condition'),
		},
		{
			id: InventoryTableColumnId.COLOR,
			title: 'Color',
			sortable: false,
			sortDirection: getSortDirection('color'),
			onSort: (direction) => handleSort('color', direction),
			renderCell: (item) => renderCell(InventoryTableColumnId.COLOR, item, isPending),
			renderHeader: createSortableHeader('Color'),
			maxWidth: 120,
			minWidth: 120,
		},
		{
			id: InventoryTableColumnId.ODOMETER,
			title: 'Odometer',
			sortable: false,
			sortDirection: getSortDirection('odometerReading'),
			onSort: (direction) => handleSort('odometerReading', direction),
			renderCell: (item) => renderCell(InventoryTableColumnId.ODOMETER, item, isPending),
			renderHeader: createSortableHeader('Odometer'),
		},
		{
			id: InventoryTableColumnId.MAP,
			title: 'MAP',
			sortable: false,
			sortDirection: getSortDirection('pricing.map'),
			onSort: (direction) => handleSort('pricing.map', direction),
			renderCell: (item) => renderCell(InventoryTableColumnId.MAP, item, isPending),
			renderHeader: createSortableHeader('MAP'),
		},
		{
			id: InventoryTableColumnId.RETAIL,
			title: 'Retail',
			sortable: false,
			sortDirection: getSortDirection('pricing.retailPrice'),
			onSort: (direction) => handleSort('pricing.retailPrice', direction),
			renderCell: (item) => renderCell(InventoryTableColumnId.RETAIL, item, isPending),
			renderHeader: createSortableHeader('Retail'),
		},
		{
			id: InventoryTableColumnId.STOCK_NUMBER,
			title: 'Stock number',
			sortable: false,
			sortDirection: getSortDirection('stockNumber'),
			onSort: (direction) => handleSort('stockNumber', direction),
			renderCell: (item) => renderCell(InventoryTableColumnId.STOCK_NUMBER, item, isPending),
			renderHeader: createSortableHeader('Stock number'),
		},
		{
			id: InventoryTableColumnId.VIN,
			title: 'VIN',
			sortable: false,
			sortDirection: getSortDirection('vin'),
			onSort: (direction) => handleSort('vin', direction),
			renderCell: (item) => renderCell(InventoryTableColumnId.VIN, item, isPending),
			renderHeader: createSortableHeader('VIN'),
		},
		{
			id: InventoryTableColumnId.CATEGORY,
			title: 'Category',
			sortable: false,
			sortDirection: getSortDirection('vehicleCategory'),
			onSort: (direction) => handleSort('vehicleCategory', direction),
			renderCell: (item) => renderCell(InventoryTableColumnId.CATEGORY, item, isPending),
			renderHeader: createSortableHeader('Category'),
		},
		{
			id: InventoryTableColumnId.FULFILLMENT_OPTIONS,
			title: 'Fulfillment options',
			sortable: false,
			sortDirection: getSortDirection('fulfillmentOptions'),
			onSort: (direction) => handleSort('fulfillmentOptions', direction),
			renderCell: (item) =>
				renderCell(InventoryTableColumnId.FULFILLMENT_OPTIONS, item, isPending, {
					servicesProvider,
				}),
			renderHeader: createSortableHeader('Fulfillment options'),
		},
		{
			id: InventoryTableColumnId.ACTIONS,
			title: '',
			sortable: false,
			renderCell: (item) =>
				renderCell(InventoryTableColumnId.ACTIONS, item, isPending, {
					onEdit: handleEditItem,
				}),
			renderHeader: () => null, // No header content
		},
	];

	// When isPending, create skeleton items that match the expected number of rows
	// This ensures consistent table height between isPending and loaded states
	const getDisplayItems = () => {
		if (isPending) {
			const getSkeletonRowCount = () => {
				if (queryCount === 0) {
					// Initial load - we don't know total items yet, show full page
					return itemsPerPage;
				}

				// Calculate items on current page
				const startIndex = (currentPage - 1) * itemsPerPage;
				const remainingItems = queryCount - startIndex;
				return Math.min(itemsPerPage, remainingItems);
			};

			return Array.from({ length: getSkeletonRowCount() }, (_, index) => ({
				id: `skeleton-${index}`,
			})) as unknown as InventoryItemSearchRecord[];
		}

		return items;
	};

	// Check if we have empty results (no isPending, no data)
	const hasEmptyResults = !isPending && items.length === 0;

	// Handler to clear all filters and search query
	const handleClearFilters = () => {
		resetAllFilters();
		updateSearchValue('');
		setCurrentPage(1);
	};

	// Check if there are any filters or search query applied
	const hasFiltersOrQuery = hasAppliedFilters || params.query;

	if (hasEmptyResults) {
		return (
			<div
				style={{
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					gap: '24px',
				}}>
				<EmptySearchResults
					title={
						params.query
							? `No vehicles found matching "${params.query}"`
							: 'No vehicles found'
					}
				/>
				{hasFiltersOrQuery && (
					<Button variant='secondary' size='small' onPress={handleClearFilters}>
						Clear filters
					</Button>
				)}
			</div>
		);
	}

	return (
		<>
			<Table<InventoryItemSearchRecord>
				columns={columns}
				items={getDisplayItems()}
				getItemId={(item) => item.id}
				selectionMode='multiple'
				selectedItems={selectedInventory}
				onSelectionChange={updateSelectedInventory}
				onRowActivate={handleRowActivate}
				aria-label={isPending ? 'Loading inventory table' : 'Inventory table'}
				stickyLeftCount={1}
				stickyRightCount={1}
				belowTableContent={
					!hasEmptyResults &&
					!isPending && (
						<Pagination
							variant='contained'
							currentPage={currentPage}
							itemsPerPage={itemsPerPage}
							totalItems={queryCount}
							itemsPerPageOptions={[10, 25, 50, 100]}
							onNavigateTo={handlePageChange}
							onItemsPerPageChange={handleItemsPerPageChange}
							aria-label='Inventory table pagination'
						/>
					)
				}
			/>
			<FloatingActionMenu
				isOpen={selectedCount > 0}
				onClose={clearSelection}
				closeButtonTooltip='Deselect all'
				leftContent={
					<Text size='bodySmall' weight='semibold' padding={{ right: '48' }}>
						{selectedCount} selected
					</Text>
				}
				rightContent={
					<Group noWrap itemsSpacing='8'>
						<ContentWithTooltip
							content={
								<Button
									size='medium'
									variant='secondary'
									rounding='10'
									onPress={handleOpenArchiveModal}
									icon={<Bin24 />}
								/>
							}
							tooltipContent='Archive'
							tooltipProps={{ placement: 'top' }}
						/>
						<Button
							size='medium'
							variant='secondary'
							rounding='10'
							onPress={handleOpenSyncModal}>
							Sync
						</Button>
						<Button
							size='medium'
							variant='secondary'
							rounding='10'
							onPress={handleOpenEnableOnlineSalesModal}>
							Enable online sales
						</Button>
						<Button
							size='medium'
							variant='secondary'
							rounding='10'
							onPress={handleOpenPublishModal}>
							Publish
						</Button>
					</Group>
				}
			/>

			{/* Sync Confirmation Modal */}
			<SyncConfirmationModal
				isOpen={isSyncModalOpen}
				onOpenChange={setIsSyncModalOpen}
				selectedIds={selectedIds}
				totalSelected={selectedCount}
				onConfirm={handleSyncConfirm}
				onCancel={handleSyncCancel}
				syncInitiatedCount={syncInitiatedCount}
				isSyncing={isSyncing}
			/>

			{/* Enable Online Sales Modal */}
			<EnableOnlineSalesModal
				isOpen={isEnableOnlineSalesModalOpen}
				onOpenChange={setIsEnableOnlineSalesModalOpen}
				selectedIds={selectedIds}
				totalSelected={selectedCount}
				onConfirm={handleEnableOnlineSalesConfirm}
				onCancel={handleEnableOnlineSalesCancel}
				enableInitiatedCount={enableInitiatedCount}
				isEnabling={isEnablingOnlineSales}
			/>

			{/* Archive Inventory Modal */}
			<ArchiveInventoryModal
				isOpen={isArchiveModalOpen}
				onOpenChange={setIsArchiveModalOpen}
				totalSelected={selectedCount}
				onConfirm={handleArchiveConfirm}
				onCancel={handleArchiveCancel}
				isArchiving={isArchiving}
			/>

			{/* Publish Confirmation Modal */}
			<PublishConfirmationModal
				isOpen={isPublishModalOpen}
				onOpenChange={setIsPublishModalOpen}
				selectedIds={selectedIds}
				totalSelected={selectedCount}
				onConfirm={handlePublishConfirm}
				onCancel={handlePublishCancel}
				publishInitiatedCount={publishInitiatedCount}
				isPublishing={isPublishingSelected}
			/>
		</>
	);
};
